import { useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import { Alert, Box, Button, Card, InputAdornment, TextField, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

// components
import StatsRow from "../../components/StatsRow";
import ExploreTable from "../../components/ExploreTable";

// data
import { useExplorePools, useExploreTokens, useProtocolStats } from "../../hooks/explore";
import { formatCount, formatPriceUSD, formatUSD } from "../../utils/format";
import { poolColumns, poolHref, searchPools, searchTokens, tokenColumns, tokenHref } from "./columns";

// pools below this USD liquidity are folded away unless the user asks for them or searches
const SMALL_POOL_USD = 1;

const tabSx = {
  position: "relative",
  px: 0,
  minWidth: 0,
  height: 48,
  borderRadius: 0,
  color: "text.secondary",
  fontSize: "1rem",
  fontWeight: 600,
  "&:hover": { color: "text.primary", backgroundColor: "transparent" },
  "&.active": { color: "text.primary" },
  "&.active::after": {
    content: '""',
    position: "absolute",
    left: 0,
    right: 0,
    bottom: -1,
    height: 2,
    borderRadius: "2px",
    backgroundColor: "primary.main",
  },
};

export const ProtocolStats = () => {
  const protocol = useProtocolStats();
  // "—" when the API fails, a skeleton while it loads
  const value = (format) => (protocol.isError ? "—" : protocol.data ? format(protocol.data) : null);
  return (
    <StatsRow
      items={[
        { label: "Total value locked", value: value((data) => formatUSD(data.totalLiquidityUSD)) },
        { label: "Total volume", value: value((data) => formatUSD(data.totalVolumeUSD)) },
        { label: "Transactions", value: value((data) => formatCount(data.txCount)) },
        { label: "KOIN price", value: value((data) => formatPriceUSD(data.koinPriceUSD)) },
      ]}
    />
  );
};

const LoadError = ({ what, onRetry }) => (
  <Box sx={{ p: 2.5 }}>
    <Alert
      severity="error"
      action={
        <Button color="inherit" size="small" onClick={onRetry}>
          Retry
        </Button>
      }
    >
      Could not load the {what} from the KoinDX API.
    </Alert>
  </Box>
);

const TokensTab = ({ query }) => {
  const tokens = useExploreTokens();
  const rows = useMemo(() => searchTokens(tokens.data || [], query), [tokens.data, query]);
  if (tokens.isError && !tokens.data) return <LoadError what="tokens" onRetry={() => tokens.refetch()} />;
  return (
    <ExploreTable
      label="Tokens"
      columns={tokenColumns}
      rows={rows}
      rowHref={tokenHref}
      initialSort={{ id: "tvl", order: "desc" }}
      loading={tokens.isLoading}
      emptyText={`No tokens match "${query.trim()}".`}
    />
  );
};

const PoolsTab = ({ query }) => {
  const pools = useExplorePools();
  const [showSmall, setShowSmall] = useState(false);
  const searching = query.trim() !== "";
  const all = useMemo(() => searchPools(pools.data || [], query), [pools.data, query]);
  const small = all.filter((pool) => Number(pool.reserveUSD) < SMALL_POOL_USD);
  const rows = showSmall || searching ? all : all.filter((pool) => Number(pool.reserveUSD) >= SMALL_POOL_USD);
  if (pools.isError && !pools.data) return <LoadError what="pools" onRetry={() => pools.refetch()} />;
  return (
    <>
      <ExploreTable
        label="Pools"
        columns={poolColumns}
        rows={rows}
        rowHref={poolHref}
        initialSort={{ id: "tvl", order: "desc" }}
        loading={pools.isLoading}
        emptyText={`No pools match "${query.trim()}".`}
      />
      {!pools.isLoading && !searching && small.length ? (
        <Box sx={{ display: "flex", justifyContent: "center", borderTop: 1, borderColor: "divider", py: 1.5 }}>
          <Button color="inherit" sx={{ color: "text.secondary" }} onClick={() => setShowSmall(!showSmall)}>
            {showSmall ? "Hide pools under $1" : `Show ${small.length} pools under $1`}
          </Button>
        </Box>
      ) : null}
    </>
  );
};

/**
 * Explore: protocol figures, then the tokens or the pools of KoinDX (tab = "tokens" | "pools")
 */
const Explore = ({ tab }) => {
  const [query, setQuery] = useState("");
  return (
    <Box sx={{ maxWidth: 960, mx: "auto", pb: { xs: 10, md: 6 } }}>
      <Typography variant="h4" component="h1" sx={{ mb: 2.5 }}>
        Explore
      </Typography>
      <ProtocolStats />
      <Card sx={{ mt: 3 }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            columnGap: 2,
            rowGap: 1.5,
            px: 2.5,
            pt: 1,
            pb: { xs: 2, sm: 1 },
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Box component="nav" aria-label="Explore" sx={{ display: "flex", gap: 3 }}>
            <Button component={NavLink} to="/explore/tokens" sx={tabSx}>
              Tokens
            </Button>
            <Button component={NavLink} to="/explore/pools" sx={tabSx}>
              Pools
            </Button>
          </Box>
          <TextField
            size="small"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={tab === "tokens" ? "Search tokens" : "Search pools"}
            sx={{ width: { xs: "100%", sm: 280 } }}
            slotProps={{
              htmlInput: { "aria-label": tab === "tokens" ? "search tokens" : "search pools" },
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>
        {tab === "tokens" ? <TokensTab query={query} /> : <PoolsTab query={query} />}
      </Card>
    </Box>
  );
};

export default Explore;
